import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Clipboard, Copy, ExternalLink, Library, Search, ScrollText } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ScriptureHubNav from "../components/ScriptureHubNav";
import { useAuth } from "../context/AuthContext";
import commandData from "../data/turahCommands.json";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

async function writeConfig() {
  let csrf = getCookie("ysq_csrf");
  if (!csrf) {
    const response = await axios.get(API + "/auth/csrf", { withCredentials: true });
    csrf = response.data?.csrf_token || getCookie("ysq_csrf");
  }
  return { withCredentials: true, headers: csrf ? { "X-CSRF-Token": csrf } : {} };
}

export default function TurahCommands() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [openIds, setOpenIds] = useState(new Set());
  const [savedIds, setSavedIds] = useState(new Set());
  const [commands, setCommands] = useState(commandData.commands);

  useEffect(() => {
    axios.get(API + "/turah-commands")
      .then((response) => {
        if (Array.isArray(response.data) && response.data.length) {
          setCommands(response.data);
        }
      })
      .catch(() => setCommands(commandData.commands));
  }, []);

  useEffect(() => {
    if (!user) {
      setSavedIds(new Set());
      return;
    }
    axios.get(API + "/library", { withCredentials: true })
      .then((response) => setSavedIds(new Set(response.data?.command_ids || [])))
      .catch(() => setSavedIds(new Set()));
  }, [user]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return commands;
    return commands.filter((command) =>
      command.title.toLowerCase().includes(needle) ||
      String(command.id).includes(needle) ||
      command.references.some((ref) => ref.reference.toLowerCase().includes(needle))
    );
  }, [query, commands]);

  const toggleOpen = (id) => {
    setOpenIds((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSave = async (id) => {
    if (!user) {
      navigate("/login?next=" + encodeURIComponent("/turah-commands"));
      return;
    }
    const shouldSave = !savedIds.has(id);
    setSavedIds((current) => {
      const next = new Set(current);
      shouldSave ? next.add(id) : next.delete(id);
      return next;
    });
    try {
      await axios.put(API + "/library/commands/" + id, { saved: shouldSave }, await writeConfig());
    } catch {
      setSavedIds((current) => {
        const next = new Set(current);
        shouldSave ? next.delete(id) : next.add(id);
        return next;
      });
    }
  };

  const copyCommand = async (command) => {
    const refs = command.references.map((ref) => ref.reference).join("; ");
    await navigator.clipboard.writeText(command.id + ". " + command.title + "\n" + refs);
  };

  const copyAll = async () => {
    const text = commands.map((command) => {
      const refs = command.references.map((ref) => ref.reference).join("; ");
      return command.id + ". " + command.title + " — " + refs;
    }).join("\n");
    await navigator.clipboard.writeText(text);
  };

  return (
    <div className="min-h-screen bg-parchment" data-testid="turah-commands-page">
      <ScriptureHubNav />

      <section className="border-b-4 border-kingdom-gold bg-royal-blue text-white">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-kingdom-gold">
                <ScrollText className="h-6 w-6" />
                <span className="text-xs font-black uppercase tracking-[0.2em]">Turah reference library</span>
              </div>
              <h1 className="mt-3 font-heading text-4xl sm:text-5xl">157 Commands</h1>
              <p className="mt-3 max-w-2xl text-white/80">
                Search the command list and open its Scripture references in YAH Scriptures.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <button onClick={copyAll} className="inline-flex min-h-10 items-center gap-2 border border-white/40 px-4 py-2 text-sm font-semibold hover:bg-white/10">
                <Clipboard className="h-4 w-4" /> Copy all
              </button>
              <Link
                to={user ? "/my-library" : "/login?next=/my-library"}
                className="inline-flex min-h-10 items-center gap-2 bg-kingdom-gold px-4 py-2 text-sm font-bold text-royal-blue"
              >
                <Library className="h-4 w-4" /> My Library
              </Link>
            </div>
          </div>
        </div>
      </section>

      <main className="mx-auto max-w-4xl px-3 py-6 sm:px-6 sm:py-8">
        <div className="sticky top-0 z-20 bg-parchment pb-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search commands or references…"
              className="min-h-12 w-full rounded-lg border border-slate-300 bg-white pl-12 pr-4 text-base outline-none focus:border-royal-blue"
            />
          </div>
          <div className="mt-2 text-xs font-semibold text-slate-500">
            {filtered.length} of {commands.length || commandData.count} commands
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {filtered.map((command) => {
            const open = openIds.has(command.id);
            const saved = savedIds.has(command.id);
            return (
              <article key={command.id} className="border-b border-slate-200 last:border-b-0">
                <div className="flex items-start gap-2 px-3 py-3 sm:gap-3 sm:px-4">
                  <button
                    type="button"
                    onClick={() => toggleOpen(command.id)}
                    className="flex min-w-0 flex-1 items-start gap-3 text-left"
                    aria-expanded={open}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-kingdom-gold font-bold text-royal-blue">
                      {command.id}
                    </span>
                    <span className="min-w-0 flex-1 pt-1 text-sm font-semibold leading-5 text-slate-800 sm:text-base">
                      {command.title}
                    </span>
                    <span className="mt-1 flex shrink-0 items-center gap-1 sm:gap-2">
                      <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                        {command.references.length}
                      </span>
                      {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => copyCommand(command)}
                    className="mt-1 hidden shrink-0 p-2 text-slate-500 hover:text-royal-blue sm:block"
                    aria-label={"Copy command " + command.id}
                  >
                    <Copy className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => toggleSave(command.id)}
                    className={"mt-1 shrink-0 rounded-md px-2 py-1.5 text-xs font-bold " + (saved ? "bg-royal-blue text-white" : "border border-slate-300 text-slate-600")}
                  >
                    {saved ? "Saved" : "Save"}
                  </button>
                </div>

                {open && (
                  <div className="bg-slate-50 px-5 pb-5 pt-1 sm:pl-[68px] sm:pr-6">
                    <div className="mb-2 text-xs font-black uppercase tracking-[0.16em] text-slate-500">References</div>
                    <div className="flex flex-wrap gap-2">
                      {command.references.map((ref, index) => (
                        ref.url ? (
                          <a
                            key={ref.reference + "-" + index}
                            href={ref.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-md border border-kingdom-gold/40 bg-white px-3 py-2 text-sm font-semibold text-royal-blue hover:bg-kingdom-gold/10"
                          >
                            {ref.reference} <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : (
                          <span key={ref.reference + "-" + index} className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-700">
                            {ref.reference}
                          </span>
                        )
                      ))}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>

        <p className="mt-5 text-center text-xs text-slate-500">
          Command titles and references are preserved from the supplied YAH Scriptures command list.
        </p>
      </main>
    </div>
  );
}
